import './RefinanciarWizard.scss';

import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../context/TenantContext';
import { useFeatureFlags } from '../../context/FeatureFlagsContext';
import { BackButton, ContinueButton } from '../../components/buttons/Buttons';
import EntradaMoneda from '../../components/currencyInput/EntradaMoneda';
import Popup from '../../components/popup/Popup';
import ConsentBox from '../../components/consentBox/ConsentBox';
import { confirmOption, simulateCredit, updateClientData } from '../../services/apiService';
import {
  getFromSessionStorage,
  removeSessionStorage,
  saveToSessionStorage,
} from '../../services/storageService';
import { footAmoutMessage, validarPie } from '../../utils/formUtils';
import useTrackEvent from '../../utils/trackEvent';
import values from '../../utils/values.json';

const MONTO_DEUDA_TEXT =
  'El monto de la deuda es el saldo que aún te queda por pagar de tu crédito automotriz. No corresponde al valor total del vehículo, sino al precio del auto menos el pie que pagaste al inicio y las cuotas que ya has abonado.';

const SinDetalle = ({ data, onBack }) => {
  const hasPushedPageView = useRef(false);
  const { pushCustomEvent } = useTrackEvent();
  const { isMeliTenant } = useTenant();
  const { flags, isLoadingFlags } = useFeatureFlags();
  const isConsentEnabled = flags.isConsentEnabled;
  const executiveRut = !isLoadingFlags ? flags.executiveRut : undefined;
  const sellerRut = !isLoadingFlags ? flags.sellerRut : undefined;
  const dealerRut = !isLoadingFlags ? flags.dealerRut : undefined;
  const storeCode = !isLoadingFlags ? flags.storeCode : undefined;
  const storeCodeMeli = !isLoadingFlags ? flags.storeCodeMeli : undefined;
  const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
  const storedFormData = JSON.parse(sessionStorage.getItem('formData')) || {};
  const [formData, setFormData] = useState({
    id: parsedData?.id || storedFormData?.id,
    rut: parsedData?.rut || storedFormData?.rut || '',
    dv: parsedData?.dv || storedFormData?.dv || '',
    name: parsedData?.name || storedFormData?.name || '',
    surnames: parsedData?.surnames || storedFormData?.surnames || '',
    phone: parsedData?.phone || storedFormData?.phone || '',
    email: parsedData?.email || storedFormData?.email || '',
    typeFinancing: parsedData?.typeFinancing || storedFormData?.typeFinancing || '',
    type: parsedData?.type || storedFormData?.type || 'CF',
    workerType: parsedData?.workerType || storedFormData?.workerType || '',
    salary: parsedData?.salary || storedFormData?.salary || 0,
    startWorkingDate: parsedData?.startWorkingDate || storedFormData?.startWorkingDate || '',
    carValue: parsedData?.carValue || storedFormData?.carValue || '',
    footAmount: parsedData?.footAmount || storedFormData?.footAmount || '',
    caryear: parsedData?.caryear || storedFormData?.caryear || '',
    registrationNumber: parsedData?.registrationNumber || storedFormData?.registrationNumber || '',
    brand: parsedData?.brand || storedFormData?.brand || '',
    model: parsedData?.model || storedFormData?.model || '',
    fee: parsedData?.fee || storedFormData?.fee || '',
    paidFees: parsedData?.paidFees || storedFormData?.paidFees || '',
    monthlyInstallmentValue:
      parsedData?.monthlyInstallmentValue || storedFormData?.monthlyInstallmentValue || '',
    amountFinancedCalculated:
      parsedData?.amountFinancedCalculated || storedFormData?.amountFinancedCalculated || 0,
    entidad: parsedData?.entidad || storedFormData?.entidad || values.institucion?.[0]?.value || '',
    consentChecked: parsedData?.consentChecked || storedFormData?.consentChecked || false,
  });

  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [selectedYear, setSelectedYear] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    const storedFormData = JSON.parse(sessionStorage.getItem('formData'));
    if (storedFormData) {
      setFormData(storedFormData);
      setSelectedYear(storedFormData.caryear || '');
    }

    if (!hasPushedPageView.current) {
      pushCustomEvent('vpv', {
        flujo: 'Refinanciamiento',
        step: '2',
        page: '/refinancia-tu-credito/info-vehiculo',
        PageName: 'Información de vehículo',
        estado: 'no conoce',
        version: 'v2',
      });

      hasPushedPageView.current = true;
    }
  }, []);

  const cleanCurrencyValue = (value) => {
    return parseFloat(value.replace(/[^\d,-]/g, '').replace(',', '.')) || 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const rawValue = cleanCurrencyValue(value).toString();

    let maxLength = 15;
    if (name === 'carValue') maxLength = 9;
    if (name === 'footAmount') maxLength = 8;
    if (name === 'monthlyInstallmentValue') maxLength = 7;

    const limitedValue = rawValue.slice(0, maxLength);

    setFormData((prevData) => ({
      ...prevData,
      [name]: limitedValue,
    }));

    const parsedValue =
      name === 'carValue' || name === 'footAmount' || name === 'monthlyInstallmentValue'
        ? cleanCurrencyValue(limitedValue) // <-- también usamos el valor limitado aquí
        : value;

    setFormData((prevData) => ({ ...prevData, [name]: parsedValue }));
  };

  const handleFeesInputChange = (e) => {
    const { name, value } = e.target;
    const limitedValue = value.slice(0, 2);
    setFormData((prevData) => ({ ...prevData, [name]: limitedValue }));
  };

  const getYearRange = () => {
    const today = new Date();
    const currentYear = today.getFullYear();
    const currentMonth = today.getMonth(); // Enero = 0, Septiembre = 8

    const endYear = currentMonth >= 8 ? currentYear + 1 : currentYear;
    const startYear = endYear - 7; // 8 años en total (incluyendo endYear)

    const years = [];
    for (let year = startYear; year <= endYear; year++) {
      years.push(year);
    }
    return Array.from({ length: endYear - startYear + 1 }, (_, i) => endYear - i); // Invertir el orden
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    setFormData((prevData) => ({ ...prevData, caryear: year }));
  };

  const handleTooltipClick = () => {
    setShowTooltip(!showTooltip);
  };

  const handleConsentChange = (e) => {
    setFormData((prevData) => ({ ...prevData, consentChecked: e.target.checked }));
  };

  const calculateAmountFinanced = ({ carValue = 0, footAmount = 0, fee = 0 }) => {
    const fixedCosts = 7735 + 23000 + 105093 + 130900 + 82230 + 337000;
    const cesantiaInsurance = fee * 8438;
    const baseAmount = carValue - footAmount + fixedCosts + cesantiaInsurance;
    const desgravamenRate = 0.000789995949004709;
    const desgravamenInsurance = desgravamenRate * baseAmount * fee;
    return baseAmount + desgravamenInsurance;
  };

  const handleNextStep = async () => {
    const validationError = validateForm();

    if (validationError) {
      setError(validationError);
      return;
    }

    const eventData = {
      flujo: 'Refinanciamiento',
      button_step_value: '2',
      step: '2',
      page: '/refinancia-tu-credito/info-vehiculo',
      PageName: 'Información de vehículo',
      button_step_name: 'continuar',
      estado: 'no conoce',
      version: 'v2',
    };

    pushCustomEvent('event_buttons', eventData);
    setIsLoading(true);
    setError(null);

    try {
      const updatedData = {
        ...formData,
        model: formData.model,
        brand: formData.brand,
        footAmount: formData.footAmount,
        monthlyInstallmentValue: formData.monthlyInstallmentValue,
        caryear: formData.caryear,
        fee: formData.fee,
        paidFees: formData.paidFees,
      };

      saveToSessionStorage('formData', updatedData);

      await updateClientData(updatedData.id, updatedData);

      const amountFinanced = calculateAmountFinanced({
        carValue: formData.carValue,
        footAmount: formData.footAmount,
        fee: formData.fee,
      });

      formData.amountFinancedCalculated = amountFinanced;

      const simulationDataStored = getFromSessionStorage('simulationData');
      let simulationData = simulationDataStored ?? null;

      if (!simulationData) {
        simulationData = await simulateCredit({
          ...formData,
          amountFinanced,
          sourceName: isMeliTenant ? 'MercadoLibre' : 'Crediautos',
          channelRequest: isMeliTenant
            ? 'Mercadolibre'
            : formData?.typeFinancing
              ? `${formData.typeFinancing}_v2`
              : null,
          storeCode: isMeliTenant ? storeCodeMeli : storeCode,
          executiveRut,
          sellerRut,
          dealerRut,
        });

        const simulation = simulationData?.simulationData;
        const creditSimulation = simulation?.creditSimulation;

        if (Array.isArray(creditSimulation) && creditSimulation.length > 0) {
          const [firstOption] = creditSimulation;

          formData.option = firstOption;
          formData.simulationId = simulation.id;

          saveToSessionStorage('simulationData', simulationData);
        }
      }

      await confirmOption({
        ...formData,
        storeCode: isMeliTenant ? storeCodeMeli : storeCode,
        executiveRut,
        sellerRut,
        dealerRut,
      });

      eventData.button_step_name = 'continuar OK';
      pushCustomEvent('event_buttons', eventData);

      removeSessionStorage('simulationData');

      navigate('/refinancia-tu-credito/solicitud-exitosa', {
        state: { currentStep: 3, estado: 'no conoce' },
      });
    } catch (error) {
      eventData.button_step_name = 'continuar Error';
      pushCustomEvent('event_buttons', eventData);
      setError('¡Ups! Algo salió mal. Intenta nuevamente en unos segundos.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePreviousStep = () => {
    onBack();
  };

  const validateForm = () => {
    const carValue = parseFloat(String(formData.carValue).replace(/\D/g, ''));
    if (!carValue || carValue <= 0) {
      return 'Por favor, ingresa un valor válido para el vehículo.';
    }

    const footAmount = parseFloat(String(formData.footAmount).replace(/\D/g, ''));

    if (!validarPie(footAmount, carValue, formData.fee, selectedYear)) {
      return footAmoutMessage(carValue, selectedYear, formData.fee);
    }

    const validFees = [6, 12, 18, 24, 30, 36, 42, 48, 60];
    const fee = parseInt(formData.fee);
    if (!fee || !validFees.includes(fee)) {
      return 'Por favor, selecciona un número de cuotas totales válido (6, 12, 18, 24, 30, 36, 42, 48, 60).';
    }

    const paidFees = parseInt(formData.paidFees);

    if (isNaN(paidFees) || paidFees === null || paidFees === undefined) {
      return 'Por favor, selecciona un número de cuotas pagadas válido.';
    }

    if (paidFees > fee) {
      return 'Las cuotas pagadas no pueden ser mayores que las cuotas totales.';
    }

    return null;
  };

  return (
    <div className="info-credito">
      <h1>Información del crédito</h1>
      <p>Para continuar con tu solicitud necesitamos info de tu crédito</p>
      <form>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Selecciona tu institución *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <select
              name="entidad"
              className="form-input-column"
              value={formData.entidad}
              onChange={handleInputChange}
            >
              {values.institucion.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="formControl-root" style={{ flexDirection: 'row', alignItems: 'center' }}>
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Monto que pagaste por el vehículo *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              value={formData.carValue}
              name="carValue"
              id="carValue"
              onChange={handleInputChange}
              placeholder="$25.490.000"
              className="form-input-column"
            />
          </div>
          <span className="tooltip-trigger" onClick={() => handleTooltipClick()}>
            i
          </span>
        </div>
        <div className="cuotas-container">
          <div className="form-container">
            <div className="formControl-root">
              <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
                Cuotas pagadas *
              </label>
              <div className="outlinedInput-root textField-root inputBase-root">
                <input
                  type="text"
                  value={formData.paidFees}
                  onChange={handleFeesInputChange}
                  name="paidFees"
                  placeholder="3"
                  id="paidFees"
                  className="form-input-column"
                ></input>
              </div>
            </div>
            <div className="formControl-root">
              <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
                Cuotas totales *
              </label>
              <div className="outlinedInput-root textField-root inputBase-root">
                <input
                  step="1"
                  value={formData.fee}
                  onChange={handleFeesInputChange}
                  placeholder="36"
                  type="text"
                  name="fee"
                  id="fee"
                  className="form-input-column"
                ></input>
              </div>
            </div>
          </div>
        </div>
        {showTooltip && (
          <Popup
            title={'¿Qué es el monto de la deuda?'}
            closePopup={handleTooltipClick}
            textContent={MONTO_DEUDA_TEXT}
          />
        )}

        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Total del pie *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              id="footAmount"
              name="footAmount"
              value={formData.footAmount}
              onChange={handleInputChange}
              placeholder="$8.000.000"
              className="form-input-column"
            />
          </div>
        </div>
        <div className="formControl-root">
          <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
            Valor cuota mensual *
          </label>
          <div className="outlinedInput-root textField-root inputBase-root">
            <EntradaMoneda
              id="monthlyInstallmentValue"
              name="monthlyInstallmentValue"
              value={formData.monthlyInstallmentValue}
              onChange={handleInputChange}
              placeholder="$455.000"
              className="form-input-column"
            />
          </div>
        </div>
        <div className="cuotas-container">
          <div className="form-container">
            <div className="formControl-root">
              <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
                Marca *
              </label>
              <div className="outlinedInput-root textField-root inputBase-root">
                <input
                  type="text"
                  value={formData.brand}
                  onChange={handleInputChange}
                  id="brand"
                  name="brand"
                  placeholder="Ej: Maxus"
                  className="form-input-column"
                ></input>
              </div>
            </div>
            <div className="formControl-root">
              <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
                Modelo *
              </label>
              <div className="outlinedInput-root textField-root inputBase-root">
                <input
                  type="text"
                  id="model"
                  value={formData.model}
                  onChange={handleInputChange}
                  placeholder="Ej: T60"
                  name="model"
                  className="form-input-column"
                ></input>
              </div>
            </div>
          </div>
        </div>
        <div className="vehiculo-year-container">
          <div className="formControl-root">
            <label className="inputLabel-root formLabel-root inputLabel-formControl inputLabel-outlined">
              Año del vehículo *
            </label>
            <div className="outlinedInput-root textField-root inputBase-root">
              <select
                name="anno"
                className="form-input-column"
                onChange={(e) => handleYearChange(e.target.value)}
                value={selectedYear} // Vincular el valor con el estado
              >
                <option value="" disabled hidden>
                  Seleccionar año
                </option>
                {getYearRange().map((year) => (
                  <option key={year} value={year}>
                    {year}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {!isLoadingFlags && isConsentEnabled && (
          <ConsentBox checked={formData.consentChecked} onChange={handleConsentChange} />
        )}

        {
          /* Mostrar mensaje de error si existe */
          error && <div className="error-msg">{error}</div>
        }
        <div className="nav-button-container">
          <BackButton onClick={() => handlePreviousStep()} />
          <ContinueButton
            onClick={() => handleNextStep()}
            isLoading={isLoading}
            isMeliTenant={isMeliTenant}
            disabled={!formData.consentChecked}
          />
        </div>
      </form>
    </div>
  );
};

export default SinDetalle;
